import {
  Button,
  CustomSelector,
  ECustomSelector,
  Typography,
} from '@globalmed/atlas-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NavigationWithOrganizationsBody.module.css';
import { useInstitutionChange } from '@/hooks/useInstitutionChange';
import { CreateInstitutionModal } from '@/features/CreateInstitutionModal';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { useUsersStore } from '@/stores/users';
import { EOrganizationProvisioningScope } from '@/types/organizations';

/**
 * Institution switcher shown in the super-admin navigation panel. Lets the
 * current super admin pick which institution to act within.
 * @returns The institution switcher
 */
export const NavigationWithOrganizationsBody = () => {
  const { t } = useTranslation();
  const { currentInstitution, institutions } = useInstitutionsStore(
    (state) => ({
      currentInstitution: state.currentInstitution,
      institutions: state.institutions,
    }),
  );
  const currentOrganization = useOrganizationsStore(
    (state) => state.currentOrganization,
  );
  const isSuperAdmin = useUsersStore((state) => state.checkIsSuperAdmin());
  const isOrgAdmin = useUsersStore((state) =>
    state.checkIsOrgAdmin(currentOrganization?.id ?? ''),
  );
  const onInstitutionChange = useInstitutionChange();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const availableInstitutions = useMemo(() => {
    if (!currentOrganization) return institutions;

    return institutions.filter(
      (item) => item.organizationId === currentOrganization.id,
    );
  }, [institutions, currentOrganization]);

  const institutionOptions = useMemo(
    () =>
      availableInstitutions
        .map((item) => ({
          ...item,
          title: item.name,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [availableInstitutions],
  );

  const hasInstitutions = !!institutionOptions.length;
  const isInstitutionProvisioning =
    currentOrganization?.provisioning?.scope ===
    EOrganizationProvisioningScope.INSTITUTION;
  const isCreateInstitutionRestricted =
    isOrgAdmin && !isSuperAdmin && isInstitutionProvisioning;

  return (
    <div className={styles.wrapper}>
      {hasInstitutions && (
        <Typography as="span" fontWeight={500}>
          {t('admin-panel.institution')}
        </Typography>
      )}
      <div className={styles.row}>
        {hasInstitutions && (
          <CustomSelector
            sx={{
              maxWidth: !isCreateInstitutionRestricted ? '167px' : '225px',
            }}
            type={ECustomSelector.Select}
            options={institutionOptions}
            selected={currentInstitution?.id}
            onChange={onInstitutionChange}
            aria-label={t('common.select-institution')}
          />
        )}
        {!isCreateInstitutionRestricted && (
          <Button
            variant={hasInstitutions ? 'secondary' : 'primary'}
            preIcon="Plus"
            wide={!hasInstitutions}
            text={
              !hasInstitutions ? t('nav-panel.create-institution') : undefined
            }
            aria-label={
              hasInstitutions ? t('nav-panel.add-institution') : undefined
            }
            onClick={() => setIsCreateModalOpen(true)}
          />
        )}
      </div>
      {!isCreateInstitutionRestricted && (
        <CreateInstitutionModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </div>
  );
};
